/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */
$basic-teaser-small-content-margin: 24px !default;
$basic-teaser-tablet-content-margin: 32px !default;
$basic-teaser-large-content-margin: 40px !default;
$basic-teaser-x-large-content-margin: 48px !default;
$basic-teaser-default-shadow: none !default;
$basic-teaser-default-radius: 0 !default;
$basic-teaser-drop-shadow: none !default;
$basic-teaser-drop-shadow-on-hover: none !default;
$basic-teaser-mobile-radius: 0 !default;
$basic-teaser-tablet-radius: 0 !default;
$basic-teaser-desktop-radius: 0 !default;
$basic-teaser-desktop-large-radius: 0 !default;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

:host {
  display: block;
  text-align: left;
  font-family: $font-family-base;
  height: auto;
  box-shadow: $basic-teaser-drop-shadow;
  border-radius: $basic-teaser-desktop-large-radius;
  transition: filter $transition-duration ease-in-out;

  @include for-size(desktop) {
    border-radius: $basic-teaser-desktop-radius;
  }

  @include for-size(tablet) {
    border-radius: $basic-teaser-tablet-radius;
  }

  @include for-size(phone) {
    border-radius: $basic-teaser-mobile-radius;
  }

  @if $basic-teaser-desktop-radius != 0 {
    overflow: hidden;
  }
}

:host(:hover) {
  filter: drop-shadow($basic-teaser-drop-shadow-on-hover);
}

eon-ui-background {
  height: 100%;
  display: flex;
}

eon-ui-headline {
  word-wrap: break-word; // For EDGE 18
  word-break: break-word; // For Chrome, Safari, Firefox
}

@include teaserHeadlinesMargin("eon-ui-headline");

.#{$prefix}basic-teaser {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  text-decoration: none;
  overflow: hidden;
  border-radius: $basic-teaser-desktop-large-radius;

  @include for-size(desktop) {
    border-radius: $basic-teaser-desktop-radius;
  }

  @include for-size(tablet) {
    border-radius: $basic-teaser-tablet-radius;
  }

  @include for-size(phone) {
    border-radius: $basic-teaser-mobile-radius;
  }

  ::slotted(eon-ui-image) {
    transition: opacity $transition-duration ease-in-out;
  }
}

.#{$prefix}wrapper {
  position: relative;
  width: 100%;
  height: auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: v(backgroundColor);
  z-index: 1;

  &.#{$prefix}image-as-background {
    eon-ui-headline {
      --color: var(--textColor) !important; // v(...) helper doesn't work with nested vars
    }

    ::slotted(eon-ui-link) {
      --contentColor: var(--textColor) !important;
      --contentHoverColor: var(--textColor) !important;
      --contentFocusColor: var(--textColor) !important;
    }

    ::slotted(eon-ui-text),
    eon-ui-text {
      --color: var(--textColor) !important;
    }

    div.#{$prefix}pictogram-wrapper > eon-ui-pictogram {
      transition: color $transition-duration ease-in-out !important;
      --color: var(--textColor) !important;
    }

    .#{$prefix}content {
      height: 100%;
      box-sizing: border-box;
    }
  }

  &.#{$prefix}only-visible-on-hover {
    background-color: v(backgroundColor);
  }

  &.#{$prefix}ratio-2x1 {
    display: flow-root;
    position: relative;
    &::before {
      @include aspect-ratio-basic-teaser(2, 1);
    }
  }

  &.#{$prefix}ratio-16x9 {
    display: flow-root;
    position: relative;
    &::before {
      @include aspect-ratio-basic-teaser(16, 9);
    }
  }

  &.#{$prefix}ratio-1x1 {
    display: flow-root;
    position: relative;
    &::before {
      @include aspect-ratio-basic-teaser(1, 1);
    }
  }

  &.#{$prefix}ratio-3x4 {
    display: flow-root;
    position: relative;
    &::before {
      @include aspect-ratio-basic-teaser(3, 4);
    }
  }

  &.#{$prefix}ratio-4x3 {
    display: flow-root;
    position: relative;
    &::before {
      @include aspect-ratio-basic-teaser(4, 3);
    }
  }

  /*
    * Aspect ratios for tablet breakpoint
    */
  &.#{$prefix}tablet-ratio-2x1 {
    @include for-size("tablet") {
      &::before {
        @include aspect-ratio-basic-teaser(2, 1);
      }
    }
  }

  &.#{$prefix}tablet-ratio-16x9 {
    @include for-size("tablet") {
      &::before {
        @include aspect-ratio-basic-teaser(16, 9);
      }
    }
  }

  &.#{$prefix}tablet-ratio-1x1 {
    @include for-size("tablet") {
      &::before {
        @include aspect-ratio-basic-teaser(1, 1);
      }
    }
  }

  &.#{$prefix}tablet-ratio-3x4 {
    @include for-size("tablet") {
      &::before {
        @include aspect-ratio-basic-teaser(3, 4);
      }
    }
  }

  &.#{$prefix}ratio-4x3 {
    @include for-size("tablet") {
      &::before {
        @include aspect-ratio-basic-teaser(4, 3);
      }
    }
  }

  /*
    * Aspect ratios for tablet breakpoint
    */
  &.#{$prefix}mobile-ratio-2x1 {
    @include for-size("phone") {
      &::before {
        @include aspect-ratio-basic-teaser(2, 1);
      }
    }
  }

  &.#{$prefix}mobile-ratio-16x9 {
    @include for-size("phone") {
      &::before {
        @include aspect-ratio-basic-teaser(16, 9);
      }
    }
  }

  &.#{$prefix}mobile-ratio-1x1 {
    @include for-size("phone") {
      &::before {
        @include aspect-ratio-basic-teaser(1, 1);
      }
    }
  }

  &.#{$prefix}mobile-ratio-3x4 {
    @include for-size("phone") {
      &::before {
        @include aspect-ratio-basic-teaser(3, 4);
      }
    }
  }

  &.#{$prefix}mobile-4x3 {
    @include for-size("phone") {
      &::before {
        @include aspect-ratio-basic-teaser(4, 3);
      }
    }
  }

  &.#{$prefix}hover-overlay {
    cursor: pointer;

    .#{$prefix}overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.2);
      transition: all $transition-duration ease-in-out;
      visibility: hidden;
      opacity: 0;

      &.#{$prefix}color-shade {
        background-color: v(hoverBackgroundColor);
      }
    }

    &:hover {
      .#{$prefix}overlay {
        visibility: visible;
        opacity: 1;
      }
    }
  }
}

.#{$prefix}cta-wrapper {
  width: 100%;

  > slot[name="cta"] {
    display: block;
    width: 100%;
  }
}

@include teaserCtaMargin(".#{$prefix}cta-wrapper");

.#{$prefix}content {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: $basic-teaser-x-large-content-margin;

  @include for-size(desktop) {
    padding: $basic-teaser-large-content-margin;
  }

  @include for-size(tablet) {
    padding: $basic-teaser-tablet-content-margin;
  }

  @include for-size(phone) {
    padding: $basic-teaser-small-content-margin;
  }

  &.#{$prefix}content-alignment {
    &-left {
      align-items: flex-start;
      .#{$prefix}content-wrapper {
        align-items: flex-start;
      }
    }

    &-center,
    &-product {
      align-items: center;

      .#{$prefix}content-wrapper {
        align-items: center;
        text-align: center;

        .#{$prefix}pictogram-wrapper {
          align-self: center;
        }

        .#{$prefix}headline-wrapper {
          max-width: max-content;
        }
      }
    }

    &-left,
    &-product {
      justify-content: flex-end;

      .#{$prefix}content-wrapper {
        flex: 1 1 0%;
      }
      .#{$prefix}header-wrapper {
        justify-content: flex-start;
        flex: 1;
      }
      .#{$prefix}cta-wrapper {
        justify-content: flex-end;
        flex: 1;
        align-items: flex-start;
      }
    }

    &-center {
      justify-content: center;
    }
  }

  .#{$prefix}content-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 0 0 0%;
    box-sizing: border-box;
    height: 100%;
    width: 100%;

    .#{$prefix}header-wrapper,
    .#{$prefix}cta-wrapper {
      position: relative;
      display: flex;
      flex-direction: column;
    }
  }
}

.#{$prefix}subheadline {
  margin-bottom: 8px;
  font-size: 18px;
}

::slotted(eon-ui-link) {
  --contentHoverColor: var(--linkHoverColor) !important;
  --contentFocusColor: var(--textHoverColor) !important;
}

::slotted(eon-ui-linkList) {
  --linklistHoverColor: var(--linkHoverColor) !important;
  --contentFocusColor: var(--textHoverColor) !important;
}

::slotted(eon-ui-info-icon) {
  display: block;
  margin-top: 16px;
}
